/*
  Builder 2.0 Professional Installation block (PDP, under Add to Cart)

  Asks the Thumbtack proxy how many installers cover a ZIP code and reports it
  in the buy column. Information only: nothing here navigates the shopper away
  from the page, because the whole point is that they keep the cart.

  Scoped IIFE, no globals, no jQuery, no dependency on builder state. If a
  requirement ever appears that this must react to the builder, stop and
  requote, because that forks both engines.

  Tracking reuses the event names the Professional Installation page already
  sends (tt_zip_search / tt_fallback) with tt_placement "pdp", so the searches
  land in the same GA4 report Warwick is already reading.
*/
(function () {
  'use strict';

  var LOOKUP_TIMEOUT_MS = 8000;
  /* One day, per shopper, in this browser. Deliberately longer than the
     worker's ten minute edge cache, which is shared with the Professional
     Installation page and cannot hold live pro names that long. */
  var CACHE_TTL_MS = 86400000;
  /* Versioned. Entries written before the worker learned how to count carry
     total:null, and a shopper holding one would be shown the no-count wording
     for a day after the fix went out. Bumping the suffix retires them at once
     and the old key is dropped on first read. */
  var CACHE_KEY = 'fdbInstallZip.v2';
  var CACHE_KEY_OLD = 'fdbInstallZip';

  /* ---------------------------------------------------------------- GA4 */
  /* Same reason as the landing page: the store's gtag only carries Google Ads
     and the GT container, so a bare gtag event never reaches the Analytics
     property. Point it at the measurement ID with page views off, since
     Shopify's pixel is already the only source of those. */
  var ga4Ready = false;

  function ensureGa4(id) {
    if (ga4Ready) return true;
    if (!id || typeof window.gtag !== 'function') return false;
    try {
      window.gtag('config', id, { send_page_view: false });
      ga4Ready = true;
    } catch (e) {}
    return ga4Ready;
  }

  /* Never let tracking break the lookup itself. */
  function track(id, name, params) {
    try {
      if (!ensureGa4(id)) return;
      var payload = params || {};
      payload.send_to = id;
      window.gtag('event', name, payload);
    } catch (e) {}
  }

  /* ------------------------------------------------------------- caching */
  /* Holding each ZIP for a day keeps us off Thumbtack on every page view. The
     count moves by one or two day to day, so a held answer can be slightly
     behind, which Warwick accepted in exchange for not hammering their end. */
  function readCache(zip) {
    try {
      if (window.localStorage.getItem(CACHE_KEY_OLD)) {
        window.localStorage.removeItem(CACHE_KEY_OLD);
      }
      var raw = window.localStorage.getItem(CACHE_KEY);
      if (!raw) return null;
      var all = JSON.parse(raw);
      var hit = all && all[zip];
      if (!hit || (Date.now() - hit.t) > CACHE_TTL_MS) return null;
      return hit.d;
    } catch (e) { return null; }
  }

  function writeCache(zip, data) {
    try {
      var raw = window.localStorage.getItem(CACHE_KEY);
      var all = raw ? JSON.parse(raw) : {};
      var keys = Object.keys(all);
      /* Keep the store small. A shopper only ever checks a handful of ZIPs. */
      if (keys.length > 20) all = {};
      all[zip] = { t: Date.now(), d: data };
      window.localStorage.setItem(CACHE_KEY, JSON.stringify(all));
    } catch (e) {}
  }

  /* --------------------------------------------------------------- utils */
  /* tt_results already means "how many came back in this page of results" on
     the Professional Installation page and the collection block, both capped
     by TT_LIMIT. Sending Thumbtack's national total under the same name would
     mix two quantities in one column of the report Warwick reads, so the total
     goes out separately as tt_total. */
  function resultsFor(d) {
    if (d && typeof d.count === 'number') return d.count;
    return ((d && d.pros) || []).length;
  }

  function totalFor(d) {
    return (d && typeof d.total === 'number') ? d.total : null;
  }

  function tpl(str, count, place) {
    return String(str || '')
      .replace(/\{count\}/g, count)
      .replace(/\{place\}/g, place);
  }

  /* Builds the answer line as real nodes rather than innerHTML, so merchant
     copy from the Theme Editor can never inject markup. Anything the merchant
     wraps in *stars* comes out emphasised. */
  function emphasised(text) {
    var frag = document.createDocumentFragment();
    var parts = String(text).split('*');
    /* Unpaired stars would bold everything after the stray one, so treat the
       whole string as plain text rather than guessing where it should end. */
    if (parts.length % 2 === 0) {
      frag.appendChild(document.createTextNode(String(text)));
      return frag;
    }
    for (var i = 0; i < parts.length; i++) {
      if (!parts[i]) continue;
      if (i % 2 === 1) {
        var strong = document.createElement('strong');
        strong.className = 'builder-v2__install-count';
        strong.textContent = parts[i];
        frag.appendChild(strong);
      } else {
        frag.appendChild(document.createTextNode(parts[i]));
      }
    }
    return frag;
  }

  function icon(paths, cls) {
    var svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.setAttribute('viewBox', '0 0 24 24');
    svg.setAttribute('fill', 'none');
    svg.setAttribute('stroke', 'currentColor');
    svg.setAttribute('stroke-width', '2');
    svg.setAttribute('stroke-linecap', 'round');
    svg.setAttribute('stroke-linejoin', 'round');
    svg.setAttribute('aria-hidden', 'true');
    if (cls) svg.setAttribute('class', cls);
    paths.forEach(function (d) {
      var p = document.createElementNS('http://www.w3.org/2000/svg', 'path');
      p.setAttribute('d', d);
      svg.appendChild(p);
    });
    return svg;
  }

  var PEOPLE = [
    'M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2',
    'M9 3a4 4 0 1 0 0 8 4 4 0 0 0 0-8',
    'M23 21v-2a4 4 0 0 0-3-3.87',
    'M16 3.13a4 4 0 0 1 0 7.75'
  ];
  var WARN = [
    'M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z',
    'M12 9v4', 'M12 17h.01'
  ];

  /* ----------------------------------------------------------- the block */
  function init(root) {
    if (!root || root.__fdbInstallReady) return;
    root.__fdbInstallReady = true;

    var cfg = {
      endpoint: (root.getAttribute('data-endpoint') || '').replace(/\/$/, ''),
      ga4: root.getAttribute('data-ga4') || '',
      showCount: root.getAttribute('data-show-count') === 'true'
    };
    /* Wording comes from the Theme Editor. If the island ever fails to parse,
       every string would be undefined and the block would render an empty
       answer line, so fall back rather than showing the shopper nothing. */
    var FALLBACK = {
      placeholder: 'ZIP Code',
      cta: 'Check',
      zipLabel: 'ZIP code',
      loading: 'Checking your area',
      resultMany: '*{count} professional installers* cover {place}',
      resultOne: '*{count} professional installer* covers {place}',
      resultNoCount: '*Professional installers* cover {place}',
      none: 'No installers found for this ZIP code.',
      again: 'Change ZIP code',
      source: '',
      sourceLogo: '',
      sourceAlt: 'Installer ratings partner',
      sourceLogoW: 0,
      sourceLogoH: 0,
      error: 'Enter a 5 digit ZIP code.',
      down: 'We cannot check installers just now. Please try again in a moment.'
    };

    var copy = {};
    var island = root.querySelector('[data-install-copy]');
    var parsed = {};
    try { parsed = JSON.parse(island.textContent) || {}; } catch (e) { parsed = {}; }
    Object.keys(FALLBACK).forEach(function (k) {
      var v = parsed[k];
      /* The logo dimensions arrive as numbers, everything else as text, so
         guard on "usable" rather than on string, or the numbers are discarded
         and the image reserves no space. */
      var usable = (typeof v === 'string' && v !== '') || (typeof v === 'number' && !isNaN(v));
      copy[k] = usable ? v : FALLBACK[k];
    });

    var panel = root.querySelector('[data-install-panel]');
    /* States swap inside here. The actions row is a sibling of it, both inside
       the panel, so clearing a state never removes the links. */
    var stage = root.querySelector('[data-install-state]');
    var live = root.querySelector('[data-install-live]');
    if (!panel) return;

    /* The form and its error line are rendered by Liquid, so the block still
       works if this asset never loads. Only the answer, the skeleton and the
       failure notes are built here, inside [data-install-out]. */
    var form = root.querySelector('[data-install-form]');
    var field = root.querySelector('[data-install-input]');
    var errorEl = root.querySelector('[data-install-error]');
    var out = root.querySelector('[data-install-out]');

    function clear() { while (out.firstChild) out.removeChild(out.firstChild); }

    /* Change ZIP code joins the persistent How it works row rather than living
       inside the answer, so the two actions sit together in one place instead
       of straddling the attribution divider. */
    var actionsRow = root.querySelector('[data-install-actions]');

    function showAgain(on) {
      if (!actionsRow) return;
      var existing = actionsRow.querySelector('[data-install-again]');
      if (!on) { if (existing) existing.parentNode.removeChild(existing); return; }
      if (existing) return;
      var again = document.createElement('button');
      again.className = 'builder-v2__install-link';
      again.type = 'button';
      again.setAttribute('data-install-again', '');
      again.textContent = copy.again;
      again.addEventListener('click', function () {
        renderForm('', false);
        if (field) { try { field.focus({ preventScroll: true }); } catch (e) { field.focus(); } }
      });
      actionsRow.insertBefore(again, actionsRow.firstChild);
    }

    /* Assigning the same string twice is not a DOM mutation, so a repeated
       message would be announced once and then never again. Clearing first
       guarantees the second identical error is still spoken. */
    var liveTimer = null;
    function announce(text) {
      if (!live) return;
      live.textContent = '';
      window.clearTimeout(liveTimer);
      liveTimer = window.setTimeout(function () { live.textContent = text; }, 60);
    }

    function setError(on) {
      if (!errorEl || !field) return;
      errorEl.hidden = !on;
      if (on) {
        field.setAttribute('aria-invalid', 'true');
        if (errorEl.id) field.setAttribute('aria-describedby', errorEl.id);
      } else {
        field.removeAttribute('aria-invalid');
        field.removeAttribute('aria-describedby');
      }
    }

    /* -------------------------------------------------------- form state */
    /* Toggles the existing form rather than rebuilding it, so the caret, any
       selection and an in-progress IME composition all survive, and the field
       is not destroyed under a soft keyboard. */
    function renderForm(zip, showError) {
      clear();
      if (form) form.hidden = false;
      if (field && typeof zip === 'string') field.value = zip;
      setError(!!showError);
      showAgain(false);
      return field;
    }

    function renderLoading() {
      clear();
      if (form) form.hidden = true;
      setError(false);
      var wrap = document.createElement('div');
      wrap.className = 'builder-v2__install-skeleton';
      wrap.setAttribute('aria-hidden', 'true');
      var dot = document.createElement('span');
      dot.className = 'builder-v2__install-skeleton-dot';
      var bar = document.createElement('span');
      bar.className = 'builder-v2__install-skeleton-bar';
      wrap.appendChild(dot);
      wrap.appendChild(bar);
      out.appendChild(wrap);
      announce(copy.loading || 'Checking your area');
    }

    /* ------------------------------------------------------ answer state */
    function renderAnswer(zip, data) {
      var place = data.location || zip;
      var total = typeof data.total === 'number' ? data.total : null;
      var found = typeof data.count === 'number' ? data.count : (data.pros || []).length;

      /* Nothing covers the area: keep the box so a second ZIP can be tried
         without a reload, message underneath. */
      if (total === 0 || (total === null && !found)) {
        renderForm(zip, false);
        var none = document.createElement('p');
        none.className = 'builder-v2__install-none';
        none.textContent = copy.none;
        out.appendChild(none);
        announce(none.textContent);
        if (field) { try { field.focus({ preventScroll: true }); } catch (e) { field.focus(); } }
        return;
      }

      clear();
      if (form) form.hidden = true;
      setError(false);
      var result = document.createElement('div');
      result.className = 'builder-v2__install-result';
      /* The form the shopper just submitted is replaced by this, so without
         somewhere to send focus it lands on <body> and a keyboard or screen
         reader user loses their place mid page. */
      result.setAttribute('tabindex', '-1');

      var line = document.createElement('p');
      line.className = 'builder-v2__install-line';
      line.appendChild(icon(PEOPLE, 'builder-v2__install-line-icon'));

      var span = document.createElement('span');
      /* Only claim a number when the service actually returned a total. Until
         it does, the block says installers cover the area and leaves the count
         out, rather than printing the page size and calling it a count. */
      var text;
      if (cfg.showCount && total !== null) {
        text = tpl(total === 1 ? copy.resultOne : copy.resultMany, total, place);
      } else {
        text = tpl(copy.resultNoCount, '', place);
      }
      span.appendChild(emphasised(text));
      line.appendChild(span);
      result.appendChild(line);

      if (copy.source) {
        var src = document.createElement('div');
        src.className = 'builder-v2__install-source';
        if (copy.sourceLogo) {
          var img = document.createElement('img');
          img.src = copy.sourceLogo;
          img.alt = copy.sourceAlt;
          img.loading = 'lazy';
          /* Taken from the image the merchant actually picked. Baking one
             partner's proportions in made a different logo jump on load,
             inside the panel whose height everything else here holds. */
          if (copy.sourceLogoW) img.width = copy.sourceLogoW;
          if (copy.sourceLogoH) img.height = copy.sourceLogoH;
          src.appendChild(img);
        }
        var srcText = document.createElement('span');
        srcText.textContent = copy.source;
        src.appendChild(srcText);
        result.appendChild(src);
      }

      out.appendChild(result);
      showAgain(true);
      announce(text.replace(/\*/g, ''));
      /* preventScroll so moving focus never nudges the page, which would
         undo the reserved panel height doing its job. */
      try { result.focus({ preventScroll: true }); } catch (e) { result.focus(); }
    }

    /* ----------------------------------------------------- failure state */
    function renderDown(zip) {
      renderForm(zip, false);
      var msg = document.createElement('p');
      msg.className = 'builder-v2__install-down';
      msg.appendChild(icon(WARN, 'builder-v2__install-down-icon'));
      var span = document.createElement('span');
      span.textContent = copy.down;
      msg.appendChild(span);
      out.appendChild(msg);
      announce(span.textContent);
      if (field) { try { field.focus({ preventScroll: true }); } catch (e) { field.focus(); } }
    }

    /* ---------------------------------------------------------- the call */
    /* A slow first answer used to land after a faster second one and replace
       it, so the shopper saw the ZIP they had already moved on from. Every
       request carries a sequence number and only the newest one may render. */
    var reqSeq = 0;
    var inflight = null;

    function lookup(zip) {
      /* From the first search onward the panel holds the tallest state's
         height, so swapping between the answer, a retry, an empty result and a
         failure does not move the page again. Set here rather than in
         renderLoading, because a cached answer and the no-endpoint path never
         reach the loading state. Not in the stylesheet, so a shopper who never
         searches is not shown a reserved void. */
      panel.classList.add('builder-v2__install-panel--searched');
      reqSeq++;
      if (inflight) { try { inflight.abort(); } catch (e) {} inflight = null; }
      var myReq = reqSeq;
      var cached = readCache(zip);
      if (cached) {
        renderAnswer(zip, cached);
        track(cfg.ga4, 'tt_zip_search', {
          tt_zip: zip, tt_placement: 'pdp', tt_results: resultsFor(cached),
          tt_total: totalFor(cached), tt_cached: true
        });
        return;
      }

      if (!cfg.endpoint) {
        renderDown(zip);
        track(cfg.ga4, 'tt_fallback', {
          tt_zip: zip, tt_placement: 'pdp', tt_reason: 'no_endpoint'
        });
        return;
      }

      if (typeof window.fetch !== 'function') {
        renderDown(zip);
        track(cfg.ga4, 'tt_fallback', { tt_zip: zip, tt_placement: 'pdp', tt_reason: 'no_fetch' });
        return;
      }

      renderLoading();

      var ctrl = window.AbortController ? new window.AbortController() : null;
      inflight = ctrl;
      var opts = ctrl ? { signal: ctrl.signal } : {};
      /* Without AbortController the request cannot be cancelled, but the
         shopper must never be stranded on a shimmering skeleton with no form
         and no way back, so the failure state renders on the same deadline. */
      var timedOut = false;
      var timer = window.setTimeout(function () {
        if (ctrl) { try { ctrl.abort(); } catch (e) {} }
        else if (myReq === reqSeq) {
          timedOut = true;
          renderDown(zip);
          track(cfg.ga4, 'tt_fallback', { tt_zip: zip, tt_placement: 'pdp', tt_reason: 'lookup_timeout' });
        }
      }, LOOKUP_TIMEOUT_MS);

      window.fetch(cfg.endpoint + '/pros?zip=' + encodeURIComponent(zip), opts)
        .then(function (res) {
          if (!res.ok) throw new Error('http ' + res.status);
          return res.json();
        })
        .then(function (data) {
          window.clearTimeout(timer);
          if (myReq !== reqSeq || timedOut) return;
          inflight = null;
          if (!data || data.error) throw new Error(data && data.error || 'empty');
          /* Only the three values renderAnswer reads. Keeping the whole
             payload would park third party business names and profile URLs on
             the shopper's device for a day for no reason. */
          writeCache(zip, {
            location: data.location || '',
            total: typeof data.total === 'number' ? data.total : null,
            count: (data.pros || []).length
          });
          renderAnswer(zip, data);
          track(cfg.ga4, 'tt_zip_search', {
            tt_zip: zip,
            tt_placement: 'pdp',
            tt_results: resultsFor(data),
            tt_total: totalFor(data),
            tt_cached: false
          });
        })
        .catch(function (err) {
          window.clearTimeout(timer);
          if (myReq !== reqSeq || timedOut) return;
          inflight = null;
          renderDown(zip);
          track(cfg.ga4, 'tt_fallback', {
            tt_zip: zip,
            tt_placement: 'pdp',
            tt_reason: (err && err.name === 'AbortError') ? 'lookup_timeout' : 'lookup_failed'
          });
        });
    }

    /* ---------------------------------------------------------- the modal */
    var modal = root.querySelector('[data-install-modal]');
    var lastFocus = null;

    /* The dialog has to sit outside the page content it hides, otherwise
       marking that content aria-hidden also hides the dialog, because an
       aria-hidden ancestor cannot be undone by a descendant. Moving it to the
       body once makes it a sibling of everything it covers.

       Because it then lives outside the section, a Theme Editor re-render would
       leave the previous one behind. Each dialog carries the id of the block it
       belongs to, so any earlier copy is removed here rather than relying on
       the unload event firing at a useful moment. */
    if (modal) {
      var owner = modal.getAttribute('data-install-modal-for');
      if (owner) {
        Array.prototype.forEach.call(
          document.querySelectorAll('[data-install-modal-for="' + owner + '"]'),
          function (old) { if (old !== modal && old.parentNode) old.parentNode.removeChild(old); }
        );
      }
      if (modal.parentNode !== document.body) document.body.appendChild(modal);
    }

    function focusables() {
      return modal ? modal.querySelectorAll('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])') : [];
    }

    /* Put back whatever overflow was there rather than clearing it, so the
       gallery lightbox's own scroll lock survives this modal closing. */
    var priorOverflow = '';
    var hiddenSiblings = [];

    function hidePageFromScreenReaders(on) {
      if (!on) {
        hiddenSiblings.forEach(function (n) {
          if (n.had) n.el.setAttribute('aria-hidden', n.had);
          else n.el.removeAttribute('aria-hidden');
        });
        hiddenSiblings = [];
        return;
      }
      hiddenSiblings = [];
      Array.prototype.forEach.call(document.body.children, function (el) {
        if (el === modal || el.nodeName === 'SCRIPT' || el.nodeName === 'STYLE') return;
        hiddenSiblings.push({ el: el, had: el.getAttribute('aria-hidden') });
        el.setAttribute('aria-hidden', 'true');
      });
    }

    function openModal(trigger) {
      if (!modal || !modal.hidden) return;
      lastFocus = trigger || document.activeElement;
      modal.hidden = false;
      priorOverflow = document.documentElement.style.overflow;
      document.documentElement.style.overflow = 'hidden';
      hidePageFromScreenReaders(true);
      var first = focusables()[0];
      if (first) first.focus();
    }

    function closeModal() {
      if (!modal) return;
      modal.hidden = true;
      document.documentElement.style.overflow = priorOverflow;
      hidePageFromScreenReaders(false);
      if (lastFocus && lastFocus.isConnected) lastFocus.focus();
    }

    if (modal) {
      modal.addEventListener('click', function (e) {
        if (e.target === modal || (e.target.closest && e.target.closest('[data-install-close]'))) closeModal();
      });
      /* Named, so the Theme Editor re-rendering the section does not leave a
         handler behind holding a detached modal. */
      var onKeydown = function (e) {
        if (!modal.isConnected) { document.removeEventListener('keydown', onKeydown); return; }
        if (modal.hidden) return;
        if (e.key === 'Escape') { closeModal(); return; }
        if (e.key !== 'Tab') return;
        var items = focusables();
        if (!items.length) return;
        var first = items[0];
        var last = items[items.length - 1];
        if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
        else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
      };
      document.addEventListener('keydown', onKeydown);
      /* Named so it can be removed. Left anonymous, every Theme Editor
         re-render leaked another closure, and each stale one restored the
         scroll lock from its own instance, releasing a lock a live instance
         had taken. */
      var onUnload = function () {
        /* Checked on the next tick, because the section is still in the
           document while the event is dispatching. */
        window.setTimeout(teardown, 0);
      };
      document.addEventListener('shopify:section:unload', onUnload);

      function teardown() {
        if (root.isConnected) return;
        document.removeEventListener('keydown', onKeydown);
        document.removeEventListener('shopify:section:unload', onUnload);
        if (modal && modal.parentNode) modal.parentNode.removeChild(modal);
        /* Only undo what this instance did. */
        if (!modal.hidden) {
          hidePageFromScreenReaders(false);
          document.documentElement.style.overflow = priorOverflow;
        }
      }
    }

    var staticHow = root.querySelector('[data-install-how]');
    if (staticHow) staticHow.addEventListener('click', function () { openModal(staticHow); });

    if (form) {
      form.addEventListener('submit', function (e) {
        e.preventDefault();
        var value = (field.value || '').trim();
        if (!/^\d{5}$/.test(value)) {
          /* Drop whatever the last search left on screen, or the no results or
             failure note stays put and the validation error stacks under it. */
          clear();
          showAgain(false);
          setError(true);
          /* Reserve from here too: an invalid ZIP is the most likely first
             interaction and it makes the panel taller. */
          panel.classList.add('builder-v2__install-panel--searched');
          try { field.focus({ preventScroll: true }); } catch (err) { field.focus(); }
          announce(copy.error);
          return;
        }
        setError(false);
        lookup(value);
      });
    }

    renderForm('', false);
  }

  function boot(scope) {
    (scope || document).querySelectorAll('[data-install-block]').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { boot(document); });
  } else {
    boot(document);
  }

  /* Theme Editor re-renders the section on every setting change. */
  document.addEventListener('shopify:section:load', function (e) { boot(e.target); });
})();
